import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useShopSettings } from "@/context/ShopSettingsContext";
import { Search, PackageOpen } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Spinner } from "@/components/ProtectedRoute";
import { NewArrivals } from "@/components/NewArrivals";
import { CategoryNav, navTitle } from "@/components/CategoryStrip";
import { SORTS, sortProducts } from "@/lib/shop";

const FILTERS = [
  { id: "all", label: "Tutti", test: () => true },
  { id: "ITA", label: "ITA", test: (p) => p.language === "ITA" },
  { id: "JP", label: "JP", test: (p) => p.language === "JP" },
  { id: "EN", label: "EN", test: (p) => p.language === "EN" },
  { id: "CN", label: "Cinese (CN)", test: (p) => p.language === "CN" },
  { id: "Sigillato", label: "Sigillati", test: (p) => p.condition === "Sigillato" },
  { id: "Singolo", label: "Singoli", test: (p) => p.condition === "Singolo" },
  { id: "Usato", label: "Usato", test: (p) => p.condition === "Usato" },
];

export default function Shop() {
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [tree, setTree] = useState([]);
  const [params, setParams] = useSearchParams();
  const nav = useMemo(() => Object.fromEntries(["cat", "lang", "type"].filter((k) => params.get(k)).map((k) => [k, params.get(k)])), [params]);
  const setNav = (next) => setParams(next, { replace: true });
  const [sort, setSort] = useState("name_asc");
  const { showcase_mode } = useShopSettings();

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => setProducts([]));
    api.get("/catalog/tree").then(({ data }) => setTree(data)).catch(() => {});
  }, []);

  const pickNav = (next) => {
    if (next.cat && !nav.cat) document.getElementById("catalogo")?.scrollIntoView({ behavior: "smooth", block: "start" });
    setNav(next);
  };

  const visible = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter);
    const s = q.trim().toLowerCase();
    const list = (products || []).filter(
      (p) => f.test(p) && (!nav.cat || p.category === nav.cat) && (!nav.lang || p.language === nav.lang) &&
        (!nav.type || p.condition === nav.type) && (!s || `${p.name} ${p.brand} ${p.set_name || ""}`.toLowerCase().includes(s))
    );
    return sortProducts(list, sort);
  }, [products, q, filter, nav, sort]);

  const hasSets = (products || []).some((p) => p.set_name);

  return (
    <div className="mx-auto max-w-7xl px-3 pt-2 sm:px-6 lg:px-8">
      <CategoryNav tree={tree} nav={nav} onChange={pickNav} />
      {products && <NewArrivals products={products} />}
      <div id="catalogo" className="mt-8 flex scroll-mt-32 flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            {navTitle(tree, nav) || "Catalogo"}
          </h1>
          <p className="mt-1 text-sm text-white/60">{showcase_mode ? "Anime, giochi e carte TCG. Scopri gli articoli e acquistali sul nostro TikTok Shop." : "Anime, giochi e carte TCG. Scegli, aggiungi al carrello e paga online."}</p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="shop-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca articolo, marca o set"
            className="h-11 w-full rounded-full border-2 border-[#FFD60A] bg-white pl-9 pr-4 text-sm outline-none shadow-[0_0_12px_rgba(255,214,10,0.35)] focus:ring-2 focus:ring-[#A64DFF]"
          />
        </div>
        <select
          data-testid="shop-sort-select"
          aria-label="Ordina"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="h-11 w-full rounded-full border-2 border-[#A64DFF] bg-[#120a1f] px-4 text-sm font-semibold text-white outline-none sm:w-64"
        >
          {SORTS.filter(([k]) => (k !== "set" || hasSets) && !(showcase_mode && k.startsWith("price"))).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        </div>
      </div>

      <div className="-mx-3 mt-5 flex gap-2 overflow-x-auto px-3 pb-1 sm:mx-0 sm:px-0">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={`shop-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`shrink-0 rounded-full border-2 px-4 py-1.5 text-sm font-bold transition-[background-color,box-shadow,border-color] duration-150 ${
              filter === f.id
                ? "border-[#FFD60A] bg-[#FFD60A] text-[#0b0b0f] shadow-[0_0_14px_rgba(255,214,10,0.6)]"
                : "border-[#A64DFF]/70 bg-transparent text-white hover:border-[#A64DFF] hover:shadow-[0_0_12px_rgba(166,77,255,0.55)]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {products === null ? (
        <Spinner />
      ) : visible.length === 0 ? (
        <div data-testid="shop-empty" className="neon-yellow mt-6 flex flex-col items-center rounded-3xl px-6 py-16 text-center text-white">
          <PackageOpen className="h-10 w-10" />
          <div className="mt-3 font-display text-lg font-extrabold">
            {products.length === 0 ? "Nuovi articoli in arrivo" : "Nessun articolo trovato"}
          </div>
          <p className="mt-1 text-sm text-white/60">
            {products.length === 0 ? "Torna a trovarci presto o scrivici sui social." : "Prova un altro filtro o una ricerca diversa."}
          </p>
        </div>
      ) : (
        <div data-testid="product-grid" className="mt-6 grid grid-cols-2 gap-4 p-1 sm:grid-cols-3 sm:gap-6 md:grid-cols-4 lg:grid-cols-5">
          {visible.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
